import type { FuturesPick } from '../../../src/worldcup/hooks/useWorldCupPicksData';
import type {
  WorldCupFuturesOptions,
} from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import type { WorldCupMatch } from '../../../src/worldcup/types/worldcup.types';

/**
 * A rule title split into plain + emphasized runs. The emphasized run is rendered
 * in gold (Design B) to call out the key term of each rule.
 */
export interface WelcomeRulesTitleSegment {
  text: string;
  em?: boolean;
}

export const WELCOME_RULES_PREVIEW_SECTIONS = [
  {
    id: 'futures',
    titleSegments: [
      { text: 'Call your ' },
      { text: 'futures', em: true },
      { text: ' before kickoff' },
    ],
    note: 'Lock these before the tournament starts',
    noteSide: 'center',
  },
  {
    id: 'countdown',
    titleSegments: [
      { text: 'Predict the score until ' },
      { text: '1′ before', em: true },
      { text: ' kickoff' },
    ],
    note: 'Edit the score until 1′ before kickoff',
    noteSide: 'center',
  },
  {
    id: 'boost',
    titleSegments: [
      { text: 'A fresh ×2 ', },
      { text: 'every round', em: true },
    ],
    note: 'One ×2 each group round (1, 2, 3) and the Round of 32',
    noteSide: 'right',
  },
  {
    id: 'live',
    titleSegments: [
      { text: 'Regular time only — the ' },
      { text: '90′', em: true },
      { text: ' score' },
    ],
    note: 'Only the 90′ score counts',
    noteSide: 'right',
  },
] as const satisfies ReadonlyArray<{
  id: string;
  titleSegments: ReadonlyArray<WelcomeRulesTitleSegment>;
  note: string;
  noteSide: 'center' | 'right';
}>;

export type WelcomeRulesPreviewSectionId =
  typeof WELCOME_RULES_PREVIEW_SECTIONS[number]['id'];

export const WELCOME_RULES_PREVIEW_FUTURES: FuturesPick = {
  winnerTeamId: 'uruguay',
  topScorerPlayerId: 'mbappe',
};

export const WELCOME_RULES_PREVIEW_FUTURES_OPTIONS: WorldCupFuturesOptions = {
  participantTeams: [
    { id: 'uruguay', name: 'Uruguay', countryCode: 'uy' },
    { id: 'germany', name: 'Germany', countryCode: 'de' },
    { id: 'argentina', name: 'Argentina', countryCode: 'ar' },
    { id: 'brazil', name: 'Brazil', countryCode: 'br' },
  ],
  topScorers: [
    {
      id: 'mbappe',
      label: 'Kylian Mbappe',
      countryCode: 'fr',
      number: 10,
      kitColor: '#1D4ED8',
      trimColor: '#FFFFFF',
    },
    {
      id: 'vinicius',
      label: 'Vinicius',
      countryCode: 'br',
      number: 7,
      kitColor: '#EAB308',
      trimColor: '#166534',
    },
  ],
};

export const WELCOME_RULES_PREVIEW_COUNTDOWN_MATCH: WorldCupMatch = {
  id: 'welcome-countdown-match',
  homeTeam: 'Argentina',
  awayTeam: 'Portugal',
  homeCountryCode: 'ar',
  awayCountryCode: 'pt',
  kickoffTime: '2026-06-12T18:59:00.000Z',
  status: 'upcoming',
  round: '1',
  stageKey: 'first_round',
  stageLabel: 'Group Stage',
  groupLetter: 'A',
  homeGroupPosition: 1,
  awayGroupPosition: 2,
  exactPoints: 5,
  resultPoints: 2,
  prediction: {
    home: 2,
    away: 1,
  },
};

export const WELCOME_RULES_PREVIEW_BOOST_MATCH: WorldCupMatch = {
  id: 'welcome-boost-match',
  homeTeam: 'France',
  awayTeam: 'England',
  homeCountryCode: 'fr',
  awayCountryCode: 'gb-eng',
  kickoffTime: '2026-06-20T20:00:00.000Z',
  status: 'upcoming',
  round: '2',
  stageKey: 'first_round',
  stageLabel: 'Group Stage',
  groupLetter: 'C',
  homeGroupPosition: 1,
  awayGroupPosition: 2,
  exactPoints: 5,
  resultPoints: 2,
  prediction: {
    home: 1,
    away: 1,
  },
};

export const WELCOME_RULES_PREVIEW_LIVE_MATCH: WorldCupMatch = {
  id: 'welcome-live-match',
  homeTeam: 'Colombia',
  awayTeam: 'Portugal',
  homeCountryCode: 'co',
  awayCountryCode: 'pt',
  kickoffTime: '2026-06-18T19:00:00.000Z',
  status: 'live',
  round: '1',
  stageKey: 'first_round',
  stageLabel: 'Group Stage',
  groupLetter: 'B',
  exactPoints: 5,
  resultPoints: 2,
  homeScore: 0,
  awayScore: 0,
  liveMinute: 90,
  prediction: {
    home: 3,
    away: 1,
  },
};
